/* eslint-disable ember/classic-decorator-no-classic-methods, ember/no-get, ember/require-computed-property-dependencies */
import Controller from '@ember/controller';
import { tracked } from '@glimmer/tracking';
import { alias, sort, mapBy } from '@ember/object/computed';
import EmberObject, { action, computed } from '@ember/object';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import Jurisdiction from '@linc-technologies/linced-common/enums/jurisdiction';
import ProgressCategories from 'linced-app/enums/progress-categories';
import MedianCategories from 'linced-app/enums/median-categories';
import { getCurrentMilestone } from '@linc-technologies/linced-common/utils/progressions';
import moment from 'moment';

export default class SubjectController extends Controller {
  @action
  setCountMilestones(value) {
    this.countMilestones = value;
  }

  @action
  setMedianMilestones(value) {
    this.medianMilestones = value;
  }

  @action
  setProgressMilestonesMeasure(value) {
    this._progressMilestonesMeasure = value;
  }

  @action
  setProgressStepsMeasure(value) {
    this._progressStepsMeasure = value;
  }

  @service
  session;

  @service
  dates;

  @service
  options;

  @alias('session.school')
  school;

  @alias('school.jurisdiction')
  jurisdiction;

  Jurisdiction = Jurisdiction;
  ProgressCategories = ProgressCategories;
  MedianCategories = MedianCategories;
  @tracked showEthnicity = true;
  @tracked showNSN = true;
  @tracked
  learnersDialogOpen = false;
  @tracked excludeIncomplete = true;
  @tracked excludeIncompleteMedians = true;
  @tracked excludeIncompleteCounts = true;
  @tracked
  categoryLabel = '';
  @tracked progressPercentages = EmberObject.create({});
  @tracked medianPercentages = EmberObject.create({});
  // picker overrides; fall back to the school option.
  @tracked _progressMilestonesMeasure;
  @tracked _progressStepsMeasure;

  get progressMilestonesMeasure() {
    return (
      this._progressMilestonesMeasure ??
      this.options.value('progress:reporting:milestones')
    );
  }

  get progressStepsMeasure() {
    return (
      this._progressStepsMeasure ??
      this.options.value('progress:reporting:rateOfProgress')
    );
  }

  @tracked medianMilestones = 4;
  @tracked countMilestones = 4;
  @tracked
  learnersNotIncluded = 0;

  @computed('displayMilestones.[]', 'milestones.[]')
  get maxMilestones() {
    let last = this.displayMilestones.at(-1);
    if (!last) {
      return 0;
    }
    let lastIndex = this.milestones.indexOf(last);
    return lastIndex + 1;
  }

  @tracked
  selectedLearners = [];

  @computed('model.{group.name,subject.label}', 'session.school.name')
  get exportTitle() {
    return `${this.get('session.school.name')}--${
      this.model.group.name
    }--Progress Rates--${this.model.subject.label}`;
  }

  @computed('model.goalSubjects.[]')
  get subjects() {
    return this.model.goalSubjects
      .slice()
      .sort(
        (a, b) =>
          a.sequence - b.sequence ||
          (a.label || '').localeCompare(b.label || ''),
      );
  }

  @computed('model.levels.[]')
  get levels() {
    return this.model.levels
      .slice()
      .sort(
        (a, b) =>
          a.sequence - b.sequence ||
          (a.label || '').localeCompare(b.label || ''),
      );
  }

  @computed('levels.@each.id')
  get levelIds() {
    return this.levels.map((item) => item.id);
  }

  @alias('model.subject.milestones')
  milestones;

  @computed('model.learners.[]', 'milestones.[]')
  get displayMilestones() {
    let indices = [];
    this.model.learners.forEach((learner) => {
      let index = this.milestones.indexOf(
        getCurrentMilestone(
          this.milestones,
          learner,
          this.dates.rolloverMonthDate,
        ),
      );
      if (index >= 0) {
        // Don't push -1 if it wasn't found.
        indices.push(index);
      }
    });

    // Display a wide enough range to encompass all learners' current milestones,
    // potentially with some additional milestones before the lowest (for comparison).
    let min = Math.min(...indices);
    let max = Math.max(...indices);

    if (min > 0) {
      // Include a couple of previous milestones (for comparison) if available.
      min = Math.max(min - 2, 0);
    }

    // If we've still only got a few, pad up to 6 (if earlier milestones exist).
    let num = max - min + 1;
    if (num < 6 && min > 0) {
      min = Math.max(0, max - 5);
    }

    return this.milestones.slice(min, max + 1);
  }

  @alias('model.learners.length')
  numLearners;

  learnersSortOrder = Object.freeze([
    'currentYearLevel',
    'firstName',
    'lastName',
  ]);

  @sort('model.learners', 'learnersSortOrder')
  sortedLearners;

  @computed('sortedLearners', 'model.progressions.[]', 'milestones.[]')
  get learners() {
    return this.sortedLearners.reduce((learners, learner) => {
      let progressions = this.model.progressions.filter(
        (item) => item.person.id === learner.get('id'),
      );
      let data = [];
      let previousLevelIndex = -1;
      this.milestones.forEach((milestone) => {
        let level = null;
        let status = '';
        let levelIndex = -1;
        let progress = 0;
        let record = progressions.find(
          (item) => item.milestone_id === milestone.id,
        );
        if (!isEmpty(record)) {
          level = record.get('level');
          levelIndex = this.levelIds.indexOf(level.get('id'));

          if (previousLevelIndex >= 0 && levelIndex >= 0) {
            progress = levelIndex - previousLevelIndex;
            switch (true) {
              case levelIndex === previousLevelIndex:
                status = 'progress-none';
                break;
              case levelIndex < previousLevelIndex:
                status = 'progress-down';
                break;
              case levelIndex > previousLevelIndex:
                status = 'progress-up';
                break;
            }
          }

          previousLevelIndex = levelIndex;
        }

        data.push({
          person: learner,
          level,
          status,
          progress,
          milestone,
        });
      });

      learners.push({
        person: learner,
        data,
        progressions,
      });
      return learners;
    }, []);
  }

  @computed(
    'dates.{previousPreviousAcademicYearLabel,previousAcademicYearLabel,currentAcademicYearLabel}',
  )
  get displayYears() {
    let previousPreviousYear = this.dates.previousPreviousAcademicYearLabel;
    let previousYear = this.dates.previousAcademicYearLabel;
    let currentYear = this.dates.currentAcademicYearLabel;
    return [
      this.dates.halfyearDatesForAcademicYear(previousPreviousYear),
      this.dates.halfyearDatesForAcademicYear(previousYear),
      this.dates.halfyearDatesForAcademicYear(currentYear),
    ];
  }

  @computed('learners.[]', 'displayMilestones.[]', 'displayYears.[]')
  get chartData() {
    let chartDataMap = EmberObject.create({});
    this.learners.forEach((learner) => {
      this.displayYears.forEach((year) => {
        learner.progressions.forEach((progression) => {
          let level = progression.get('level');
          if (
            moment(progression.dueDate).isBetween(
              year.startDate,
              year.middleDate,
              'day',
              '[]',
            )
          ) {
            let keyMid = `${level.get('label')}_Mid${moment(
              progression.dueDate,
            ).year()}`;
            if (!chartDataMap[keyMid]) {
              chartDataMap[keyMid] = {
                level: level.get('label'),
                timeframe: `Mid ${moment(progression.dueDate).year()}`,
                students: [learner],
                sortDate: progression.dueDate,
                levelSequence: level.get('sequence'),
              };
            } else {
              if (!chartDataMap[keyMid].students.includes(learner)) {
                chartDataMap[keyMid].students.push(learner);
              }
            }
          } else if (
            moment(progression.dueDate).isBetween(
              year.middleDate,
              year.endDate,
              'day',
              '[]',
            )
          ) {
            let keyEnd = `${level.get('label')}_End${moment(
              progression.dueDate,
            ).year()}`;
            if (!chartDataMap[keyEnd]) {
              chartDataMap[keyEnd] = {
                level: level.get('label'),
                timeframe: `End ${moment(progression.dueDate).year()}`,
                students: [learner],
                sortDate: progression.dueDate,
                levelSequence: level.get('sequence'),
              };
            } else {
              if (!chartDataMap[keyEnd].students.includes(learner)) {
                chartDataMap[keyEnd].students.push(learner);
              }
            }
          }
        });
      });
    });
    // Add tooltips
    Object.values(chartDataMap).forEach((bar) => {
      bar.tooltip = `${bar.students.length} ${
        bar.students.length > 1 ? 'students' : 'student'
      } at ${bar.level} in ${bar.timeframe}`;
    });

    return Object.values(chartDataMap).sort((a, b) => {
      return moment(a.sortDate) - moment(b.sortDate);
    });
  }

  // learnerPeople provides an intermediary CP so the @each dependent key below
  // stays one level deep.
  @mapBy('learners', 'person')
  learnerPeople;

  @computed(
    'learnerPeople.@each.{id,jurisdictionalId,firstName,lastName,currentYearLevel,displayEthnicity,atsi}',
    'showNSN',
    'showEthnicity',
    'milestones.[]',
    'displayMilestones.[]',
  )
  get exportData() {
    return this.learners.map((learner) => {
      let person = learner.person.getProperties([
        'id',
        'jurisdictionalId',
        'firstName',
        'lastName',
        'currentYearLevel',
        'displayEthnicity',
        'atsi',
      ]);
      let ethnicity;
      if (this.jurisdiction === Jurisdiction.AU_NSW) {
        ethnicity = person.atsi;
      } else {
        ethnicity = person.displayEthnicity;
      }
      let totalProgress = this.calculateTotalProgress(learner.data);
      let row = [
        ...(this.showNSN ? [person.jurisdictionalId] : []),
        person.firstName,
        person.lastName,
        person.currentYearLevel,
        ...(this.showEthnicity ? [ethnicity] : []),
        totalProgress,
      ];

      let firstDisplayedMilestone = this.displayMilestones.at(0);
      let lastDisplayedMilestone = this.displayMilestones.at(-1);
      let firstIndex = this.milestones.indexOf(firstDisplayedMilestone);
      let lastIndex = this.milestones.indexOf(lastDisplayedMilestone);

      learner.data.slice(firstIndex, lastIndex + 1).forEach((data) => {
        if (isEmpty(data.level)) {
          row.push('');
          row.push('');
        } else {
          if (!isEmpty(data.status)) {
            row.push(data.level.get('label'));
            row.push(data.progress);
          } else {
            row.push(data.level.get('label'));
            row.push('');
          }
        }
      });
      return row;
    });
  }

  @computed(
    'learners.[]',
    'milestones.[]',
    'progressMilestonesMeasure',
    'progressStepsMeasure',
    'progressPercentages',
    'excludeIncomplete',
  )
  get progressPercentagesMap() {
    let belowTarget = [];
    let atTarget = [];
    let greaterThanTarget = [];
    let atOrBeyondTarget = [];
    let notIncluded = [];
    this.learners.forEach((learner) => {
      let currLearnerProgress = 0;
      let currMilestoneIndex = this.milestones.indexOf(
        getCurrentMilestone(
          this.milestones,
          learner.person,
          this.dates.rolloverMonthDate,
        ),
      );
      let learnerLastMilestoneIndex = currMilestoneIndex;
      // Ensure the loop index is also lt the learners latest milestone index
      // so, we don't add progress that's already been added to the learner
      for (
        let index = 1;
        index <= this.progressMilestonesMeasure &&
        index <= learnerLastMilestoneIndex;
        index++
      ) {
        currLearnerProgress += learner.data.at(currMilestoneIndex).progress;
        currMilestoneIndex--;
      }
      if (
        (this.hasMissingMilestones(learner, this.progressMilestonesMeasure) ||
          !this.hasStatuses(learner)) &&
        this.excludeIncomplete
      ) {
        return notIncluded.push(learner);
      }
      switch (true) {
        case currLearnerProgress > parseInt(this.progressStepsMeasure):
          greaterThanTarget.push(learner);
          atOrBeyondTarget.push(learner);
          break;
        case currLearnerProgress === parseInt(this.progressStepsMeasure):
          atTarget.push(learner);
          atOrBeyondTarget.push(learner);
          break;
        case currLearnerProgress < parseInt(this.progressStepsMeasure):
          belowTarget.push(learner);
          break;
      }
    });
    return this.buildProgressPercentagesMap(
      belowTarget,
      atTarget,
      greaterThanTarget,
      atOrBeyondTarget,
      notIncluded,
    );
  }

  @computed(
    'learners.[]',
    'milestones.[]',
    'countMilestones',
    'excludeIncompleteCounts',
    'progressByCount',
  )
  get progressByCountPercentagesMap() {
    let progressCountMap = EmberObject.create({});
    let sortedKeys = [];
    progressCountMap.set('Not Included', []);
    sortedKeys.push('Not Included');
    this.learners.forEach((learner) => {
      if (
        (this.hasMissingMilestones(learner, this.countMilestones) ||
          !this.hasStatuses(learner)) &&
        this.excludeIncompleteCounts
      ) {
        progressCountMap.get('Not Included').push(learner);
        return;
      }
      let currLearnerProgress = 0;
      let currMilestoneIndex = this.milestones.indexOf(
        getCurrentMilestone(
          this.milestones,
          learner.person,
          this.dates.rolloverMonthDate,
        ),
      );
      let learnerLastMilestoneIndex = currMilestoneIndex;
      // Ensure the loop index is also lt the learners latest milestone index
      // so, we don't add progress that's already been added to the learner
      for (
        let index = 1;
        index <= this.countMilestones && index <= learnerLastMilestoneIndex;
        index++
      ) {
        currLearnerProgress += learner.data.at(currMilestoneIndex).progress;
        currMilestoneIndex--;
      }
      // Check if the progress value already exists in the map
      if (!progressCountMap.get(currLearnerProgress)) {
        progressCountMap.set(currLearnerProgress, [learner]);
        sortedKeys.push(currLearnerProgress);
      } else {
        progressCountMap.get(currLearnerProgress).push(learner);
      }
    });
    // Sort the keys
    sortedKeys.sort((a, b) => {
      // Move 'Not Included' to the end
      if (a === 'Not Included' || b === 'Not Included') {
        return -1;
      }
      return a - b;
    });
    // Build a new array with sorted keys and values
    let sortedProgressCountArray = [];
    sortedKeys.forEach((key) => {
      sortedProgressCountArray.push({
        progressStep: key,
        learners: progressCountMap[key],
      });
    });
    this.setLearnersNotIncludedCount(
      progressCountMap.get('Not Included').length,
    );
    return sortedProgressCountArray;
  }

  setLearnersNotIncludedCount(count) {
    this.learnersNotIncluded = count;
  }

  @computed(
    'learners.[]',
    'milestones.[]',
    'displayMilestones.[]',
    'medianMilestones',
    'excludeIncompleteMedians',
    'medianProgress',
    'medianPercentages',
  )
  get medianPercentagesMap() {
    let belowMedian = [];
    let atMedian = [];
    let beyondMedian = [];
    let notIncluded = [];
    this.learners.forEach((learner) => {
      let currLearnerProgress = 0;
      let currMilestoneIndex = this.milestones.indexOf(
        getCurrentMilestone(
          this.milestones,
          learner.person,
          this.dates.rolloverMonthDate,
        ),
      );
      let learnerLastMilestoneIndex = currMilestoneIndex;
      // Ensure the loop index is also lt the learners latest milestone index
      // so, we don't add progress that's already been added to the learner
      for (
        let index = 1;
        index <= this.medianMilestones && index <= learnerLastMilestoneIndex;
        index++
      ) {
        currLearnerProgress += learner.data.at(currMilestoneIndex).progress;
        currMilestoneIndex--;
      }
      if (
        (this.hasMissingMilestones(learner, this.medianMilestones) ||
          !this.hasStatuses(learner)) &&
        this.excludeIncompleteMedians
      ) {
        notIncluded.push(learner);
      } else {
        switch (true) {
          case currLearnerProgress > parseInt(this.medianProgress):
            beyondMedian.push(learner);
            break;
          case currLearnerProgress === parseInt(this.medianProgress):
            atMedian.push(learner);
            break;
          case currLearnerProgress < parseInt(this.medianProgress):
            belowMedian.push(learner);
            break;
        }
      }
    });
    return this.buildMedianPercentagesMap(
      belowMedian,
      atMedian,
      beyondMedian,
      notIncluded,
    );
  }

  // medianProgress calculates the median progress across a set amount of milestones
  @computed(
    'learners.[]',
    'milestones.[]',
    'medianMilestones',
    'excludeIncompleteMedians',
  )
  get medianProgress() {
    let totalProgress = 0;
    let learnersNotIncluded = 0;
    this.learners.forEach((learner) => {
      let currMilestoneIndex = this.milestones.indexOf(
        getCurrentMilestone(
          this.milestones,
          learner.person,
          this.dates.rolloverMonthDate,
        ),
      );
      if (
        (this.hasMissingMilestones(learner, this.medianMilestones) ||
          !this.hasStatuses(learner)) &&
        this.excludeIncompleteMedians
      ) {
        learnersNotIncluded++;
      } else {
        let learnerLastMilestoneIndex = currMilestoneIndex;
        // Ensure the loop index is also lt the learners latest milestone index
        // so, we don't add progress that's already been added to the learner
        for (
          let index = 1;
          index <= this.medianMilestones && index <= learnerLastMilestoneIndex;
          index++
        ) {
          totalProgress += learner.data.at(currMilestoneIndex).progress;
          currMilestoneIndex--;
        }
      }
    });
    if (this.excludeIncompleteMedians) {
      return (
        Math.round(
          totalProgress / (this.learners.length - learnersNotIncluded),
        ) || 0
      );
    }
    return Math.round(totalProgress / this.learners.length);
  }

  // hasStatuses checks if the user has any status changes across milestones
  hasStatuses(learner) {
    return learner.data.some((datum) => datum.status !== '');
  }

  hasMissingMilestones(learner, milestoneSteps) {
    let hasMissing = false;
    let index = this.milestones.indexOf(
      getCurrentMilestone(
        this.milestones,
        learner.person,
        this.dates.rolloverMonthDate,
      ),
    );
    let currentMilestone = this.milestones[index];
    let progressions = this.model.progressions.filter(
      (item) => item.person.id === learner.person.get('id'),
    );
    let milestones = this.milestones.slice(
      index - milestoneSteps + 1,
      index + 1,
    );
    if (milestoneSteps > index + 1) {
      milestones = this.milestones.slice(0, index + 1);
    }

    if (milestoneSteps > milestones.length) {
      return true;
    }
    milestones.forEach((milestone) => {
      let level = null;
      let record = progressions.find(
        (item) => item.milestone_id === milestone.id,
      );
      if (!isEmpty(record)) {
        level = record.get('level');
      }
      // If the level is empty on a non-current milestone
      // learner is missing a milestone
      if (isEmpty(level) && currentMilestone !== milestone) {
        hasMissing = true;
      }
    });
    return hasMissing;
  }

  buildProgressPercentagesMap(
    belowTarget,
    atTarget,
    greaterThanTarget,
    atOrBeyondTarget,
    notIncluded,
  ) {
    this.progressPercentages.setProperties({
      [ProgressCategories.BELOW_TARGET]: belowTarget,
      [ProgressCategories.AT_TARGET]: atTarget,
      [ProgressCategories.BEYOND_TARGET]: greaterThanTarget,
      [ProgressCategories.TOTAL_AT_AND_BEYOND]: atOrBeyondTarget,
      [ProgressCategories.NOT_INCLUDED]: notIncluded,
    });
    return this.progressPercentages;
  }

  buildMedianPercentagesMap(belowMedian, atMedian, beyondMedian, notIncluded) {
    this.medianPercentages.setProperties({
      [MedianCategories.BELOW_MEDIAN]: belowMedian,
      [MedianCategories.AT_MEDIAN]: atMedian,
      [MedianCategories.BEYOND_MEDIAN]: beyondMedian,
      [MedianCategories.NOT_INCLUDED]: notIncluded,
    });
    return this.medianPercentages;
  }

  @computed('progressMilestonesMeasure', 'learners.[]')
  get totalProgressByPerson() {
    let progressByLearnerMap = {};
    this.learners.forEach((learner) => {
      progressByLearnerMap[learner.person.id] = this.calculateTotalProgress(
        learner.data,
      );
    });
    return progressByLearnerMap;
  }

  // Method to calculate total progress
  calculateTotalProgress(data) {
    let currMilestoneIndex = this.milestones.indexOf(
      getCurrentMilestone(
        this.milestones,
        data.at(0).person,
        this.dates.rolloverMonthDate,
      ),
    );
    let totalProgress = 0;
    for (
      let i = 0;
      i < this.progressMilestonesMeasure && i < currMilestoneIndex;
      i++
    ) {
      totalProgress += data
        .slice(0, currMilestoneIndex + 1)
        .reverse()
        .at(i).progress;
    }
    return totalProgress;
  }

  @action
  getDataToDisplay(data) {
    let firstDisplayedMilestone = this.displayMilestones.at(0);
    let lastDisplayedMilestone = this.displayMilestones.at(-1);
    let firstIndex = this.milestones.indexOf(firstDisplayedMilestone);
    let lastIndex = this.milestones.indexOf(lastDisplayedMilestone);
    return data.slice(firstIndex, lastIndex + 1);
  }

  @action
  getTotalProgress(data) {
    return this.calculateTotalProgress(data);
  }

  @action
  genderBreakdown(learners) {
    let counts = {
      M: 0,
      F: 0,
      N: 0,
    };
    if (learners.length < 1) {
      return counts;
    }

    learners.forEach((learner) => {
      switch (learner.person.get('gender')) {
        case 'M':
          counts.M++;
          break;
        case 'F':
          counts.F++;
          break;
        default:
          counts.N++;
          break;
      }
    });
    return {
      counts,
      percentage: {
        M: (counts.M / learners.length) * 100,
        F: (counts.F / learners.length) * 100,
      },
    };
  }

  @action
  showLearners(learners, categoryLabel) {
    let extractedLearners = [];
    learners.forEach((learner) => {
      extractedLearners.push(learner.person);
    });
    this.selectedLearners = extractedLearners;
    this.learnersDialogOpen = true;
    this.categoryLabel = categoryLabel;
  }

  @action
  closeLearnersDialog() {
    this.learnersDialogOpen = false;
  }
}
